'use client';

import { useState, useEffect, useRef } from 'react';

/**
 * Message sets for different loading contexts.
 * Messages rotate to give users a sense of progress.
 */
export const LOADING_MESSAGES = {
  // After signup, transitioning to company-info
  accountSetup: [
    'Setting up your account...',
    'Creating your workspace...',
    'Preparing your dashboard...',
    'Almost there...',
  ],
  // Loading company-info page
  companyInfo: [
    'Loading your account...',
    'Fetching your details...',
    'Setting things up...',
    'Almost ready...',
  ],
  // Loading payment-setup page (auth/session)
  paymentAuth: [
    'Loading payment setup...',
    'Preparing secure checkout...',
    'Setting up billing...',
    'Almost ready...',
  ],
  // Loading packages on payment page
  packages: [
    'Loading available packages...',
    'Fetching subscription options...',
    'Preparing your plans...',
    'Almost there...',
  ],
  // Processing payment
  paymentProcessing: [
    'Processing payment...',
    'Verifying card details...',
    'Setting up subscription...',
    'Finalizing your account...',
  ],
  // Navigating to setup-complete
  setupComplete: [
    'Finalizing your setup...',
    'Wrapping things up...',
    'Almost done...',
  ],
  // Loading demo / product tour
  demo: [
    'Loading product tour...',
    'Preparing the demo...',
    'Setting up your tour...',
    'Almost ready...',
  ],
  // Generic loading
  generic: [
    'Loading...',
    'Please wait...',
    'Working on it...',
  ],
} as const;

export type LoadingMessageContext = keyof typeof LOADING_MESSAGES;

interface UseRotatingLoadingMessageOptions {
  /** How long each message displays before rotating (ms). Default: 2500ms */
  interval?: number;
  /** Start from the beginning each time, or continue from last position */
  resetOnChange?: boolean;
}

/**
 * Hook that rotates through loading messages to give users feedback during slow operations.
 *
 * @param context - The loading context (determines which message set to use)
 * @param options - Configuration options
 * @returns The current message to display
 *
 * @example
 * ```tsx
 * const message = useRotatingLoadingMessage('accountSetup');
 * return <p>{message}</p>;
 * ```
 */
export function useRotatingLoadingMessage(
  context: LoadingMessageContext,
  options: UseRotatingLoadingMessageOptions = {}
): string {
  const { interval = 2500, resetOnChange = true } = options;
  const messages = LOADING_MESSAGES[context];
  const [index, setIndex] = useState(0);
  const previousContext = useRef(context);

  // Reset index when context changes (if resetOnChange is true)
  useEffect(() => {
    if (resetOnChange && context !== previousContext.current) {
      queueMicrotask(() => {
        setIndex(0);
      });
      previousContext.current = context;
    }
  }, [context, resetOnChange]);

  // Rotate through messages
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((current) => (current + 1) % messages.length);
    }, interval);

    return () => clearInterval(timer);
  }, [messages.length, interval]);

  return messages[index];
}

/**
 * Get the initial message for a context (useful for SSR or static display)
 */
export function getInitialLoadingMessage(context: LoadingMessageContext): string {
  return LOADING_MESSAGES[context][0];
}
